import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
    getQCObservationDetails,
    startQCObservationWork,
    resolveQCObservation,
    reviewQCObservation,
    assignQCObservationUser,
    uploadQCObservationAttachment,
    downloadQCObservationReport,
    getProjectsForCurrentUser,
} from "../../api/index";
import { resolveMediaUrl, formatObservationDateTime } from "../../lib/utils";

import {
    ArrowLeft,
    CheckCircle2,
    Clock,
    CircleDot,
    TriangleAlert,
    Camera,
    MessageSquare,
    UserCircle,
    Paperclip,
    Download,
    Eye,
    PenLine,
    UploadCloud,
    X
} from "lucide-react";
import { generateLocalObservationReportPDF } from "../../utils/generateLocalObservationReportPDF";
import SafetyImageAnnotationModal from "../Safety/Safety_Inspection/SafetyImageAnnotationModal";
import SafetyCameraCaptureModal from "../Safety/Safety_Inspection/SafetyCameraCaptureModal";

const sortLogsNewestFirst = (logs = []) =>
    [...logs].sort((a, b) => {
        const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
        if (bTime !== aTime) return bTime - aTime;
        return Number(b?.id || 0) - Number(a?.id || 0);
    });

const ACTION_LABELS = {
    created: "Observation Created",
    assigned: "Assigned",
    started: "Work Started",
    commented: "Attachment Uploaded",
    action_taken: "Action Taken",
    resolved: "Maker Submitted",
    verified: "Checker Reviewed",
    closed: "Closed",
    skipped: "Skipped",
    forwarded: "Forwarded",
};

function SelectedImagePreview({ file, alt, onRemove, onAnnotate }) {
    const [previewUrl, setPreviewUrl] = useState("");

    useEffect(() => {
        if (!file) {
            setPreviewUrl("");
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [file]);

    if (!previewUrl) return null;

    return (
        <div className="relative rounded border overflow-hidden flex flex-col w-20" style={{ borderColor: "#DEDAD1" }}>
            <img src={previewUrl} className="w-20 h-16 object-cover" alt={alt} />
            <button onClick={onRemove} className="absolute top-1 right-1 bg-white rounded-full p-0.5 text-red-500 hover:bg-red-50 flex items-center justify-center font-bold shadow" style={{ width: '18px', height: '18px', fontSize: '12px' }}>×</button>
            <button
                type="button"
                onClick={onAnnotate}
                className="flex items-center justify-center gap-1.5 px-1 py-1 text-[10px] font-semibold"
                style={{ borderColor: "#F0C28F", color: "#B86519", background: "#FFF8F0", borderTopWidth: "1px" }}
            >
                <PenLine size={12} />
                Annotate
            </button>
        </div>
    );
}

const STATUS_CONFIG = {
    pending: { label: "Pending", fg: "#C1443A", bg: "#F5DCD9", icon: TriangleAlert, step: 1 },
    in_progress: { label: "In Progress", fg: "#B4761F", bg: "#F6E6CE", icon: Clock, step: 2 },
    resolved: { label: "Resolved", fg: "#3F8358", bg: "#DCEBE1", icon: CheckCircle2, step: 3 },
    closed: { label: "Closed", fg: "#6B7280", bg: "#E7E5E0", icon: CircleDot, step: 4 },
};

const normalizeStatusKey = (val) => {
    if (!val) return "";
    const s = String(val).trim().toLowerCase().replace(/[\s_-]+/g, "_");
    if (s === "wip" || s === "in_review" || s === "inprogress") return "in_progress";
    if (s === "complete" || s === "completed" || s === "done") return "closed";
    return s;
};

function StatusPill({ status }) {
    const key = normalizeStatusKey(status) || "pending";
    const tone = STATUS_CONFIG[key] || STATUS_CONFIG.pending;
    const Icon = tone.icon;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
            style={{ color: tone.fg, background: tone.bg }}
        >
            <Icon size={14} strokeWidth={2.5} />
            {tone.label}
        </span>
    );
}

function Stepper({ currentStatus }) {
    const currentStep = STATUS_CONFIG[currentStatus]?.step || 1;
    const steps = [
        { key: "pending", label: "Pending" },
        { key: "in_progress", label: "In Progress" },
        { key: "resolved", label: "Resolved" },
        { key: "closed", label: "Closed" }
    ];

    return (
        <div className="flex items-center justify-between w-full max-w-3xl mx-auto mb-8 relative">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-[#E7E5E0] w-full z-0"></div>
            <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] z-0 transition-all duration-500"
                style={{
                    background: "#E07A1F",
                    width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`
                }}
            ></div>

            {steps.map((step, index) => {
                const stepNum = index + 1;
                const isClosed = currentStatus === "closed";
                const isCompleted = stepNum < currentStep || (isClosed && stepNum === currentStep);
                const isCurrent = stepNum === currentStep && !isClosed;
                const isUpcoming = stepNum > currentStep;

                let circleBg = "white";
                let circleBorder = "#DEDAD1";
                let circleColor = "#9A968C";

                if (isCompleted) {
                    circleBg = "#E07A1F";
                    circleBorder = "#E07A1F";
                    circleColor = "white";
                } else if (isCurrent) {
                    circleBg = "white";
                    circleBorder = "#E07A1F";
                    circleColor = "#E07A1F";
                }

                return (
                    <div key={step.key} className="relative z-10 flex flex-col items-center gap-2">
                        <div
                            className="w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-[13px] transition-colors"
                            style={{ background: circleBg, borderColor: circleBorder, color: circleColor }}
                        >
                            {isCompleted ? <CheckCircle2 size={16} /> : stepNum}
                        </div>
                        <div
                            className="text-[12px] font-semibold absolute -bottom-6 whitespace-nowrap"
                            style={{ color: isCurrent ? "#1B2430" : "#8A8F79" }}
                        >
                            {step.label}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function ObservationItemCard({ item, currentUser, reloadObservation }) {
    const [actionRemark, setActionRemark] = useState("");
    const [actionTaken, setActionTaken] = useState("");
    const [actionPhotos, setActionPhotos] = useState([]);
    const [checkerCompliancePhotos, setCheckerCompliancePhotos] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const [uploadingPhotos, setUploadingPhotos] = useState(false);
    const [annotationQueue, setAnnotationQueue] = useState([]);
    const [annotationQueueIndex, setAnnotationQueueIndex] = useState(0);
    const [cameraTarget, setCameraTarget] = useState(null);

    const addActionPhotos = (newFiles) => {
        const fileList = Array.from(newFiles || []);
        if (!fileList.length) return;

        let queueItems = [];
        setActionPhotos((prev) => {
            const previousCount = prev.length;
            queueItems = fileList.map((file, offset) => ({
                file,
                fileIndex: previousCount + offset,
                imageType: "action",
            }));
            return [...prev, ...fileList];
        });

        if (queueItems.length > 0) {
            setAnnotationQueue(queueItems);
            setAnnotationQueueIndex(0);
        }
    };

    const addCompliancePhotos = (newFiles) => {
        const fileList = Array.from(newFiles || []);
        if (!fileList.length) return;

        let queueItems = [];
        setCheckerCompliancePhotos((prev) => {
            const previousCount = prev.length;
            queueItems = fileList.map((file, offset) => ({
                file,
                fileIndex: previousCount + offset,
                imageType: "compliance",
            }));
            return [...prev, ...fileList];
        });

        if (queueItems.length > 0) {
            setAnnotationQueue(queueItems);
            setAnnotationQueueIndex(0);
        }
    };

    const handleCameraCapture = (capturedFile) => {
        if (cameraTarget === "action") {
            addActionPhotos([capturedFile]);
        } else if (cameraTarget === "compliance") {
            addCompliancePhotos([capturedFile]);
        }
        setCameraTarget(null);
    };

    const openActionPhotoAnnotation = (file, fileIndex) => {
        setAnnotationQueue([{ file, fileIndex, imageType: "action" }]);
        setAnnotationQueueIndex(0);
    };

    const openCompliancePhotoAnnotation = (file, fileIndex) => {
        setAnnotationQueue([{ file, fileIndex, imageType: "compliance" }]);
        setAnnotationQueueIndex(0);
    };

    const handleAnnotationSave = (annotatedFile) => {
        const currentItem = annotationQueue[annotationQueueIndex];
        if (!currentItem) return;

        if (currentItem.imageType === "action") {
            setActionPhotos((current) =>
                current.map((file, index) =>
                    index === currentItem.fileIndex ? annotatedFile : file
                )
            );
        } else if (currentItem.imageType === "compliance") {
            setCheckerCompliancePhotos((current) =>
                current.map((file, index) =>
                    index === currentItem.fileIndex ? annotatedFile : file
                )
            );
        }

        setAnnotationQueue((prevQueue) =>
            prevQueue.map((entry, idx) =>
                idx === annotationQueueIndex ? { ...entry, file: annotatedFile } : entry
            )
        );

        if (annotationQueueIndex < annotationQueue.length - 1) {
            setAnnotationQueueIndex((prev) => prev + 1);
        } else {
            closeAnnotationQueue();
        }
    };

    const handleAnnotationSkip = () => {
        if (annotationQueueIndex < annotationQueue.length - 1) {
            setAnnotationQueueIndex((prev) => prev + 1);
        } else {
            closeAnnotationQueue();
        }
    };

    const handleAnnotationPrevious = () => {
        setAnnotationQueueIndex((prev) => Math.max(prev - 1, 0));
    };

    const handleAnnotationNext = () => {
        if (annotationQueueIndex < annotationQueue.length - 1) {
            setAnnotationQueueIndex((prev) => prev + 1);
        }
    };

    const closeAnnotationQueue = () => {
        setAnnotationQueue([]);
        setAnnotationQueueIndex(0);
    };

    const isChecker = currentUser.role_name === "checker";
    const isMaker = currentUser.role_name === "maker";

    const isActiveAssignedMaker = isMaker && item.assignments?.some(
        a => String(a.role_name).toLowerCase() === "maker" && String(a.user_id) === String(currentUser.user_id) && a.is_active && !a.is_skipped
    );
    const isClaimedMaker = isMaker && isActiveAssignedMaker && String(item.current_actor_id) === String(currentUser.user_id);
    const isCurrentChecker = isChecker && String(item.current_actor_id) === String(currentUser.user_id);

    const hasActionControls = (item.status === 'pending' && isActiveAssignedMaker) ||
        (item.status === 'in_progress' && isClaimedMaker) ||
        (item.status === 'resolved' && isCurrentChecker);

    const sortAttachmentsNewestFirst = (attachments = []) =>
        [...attachments].sort((a, b) => {
            const aTime = a?.uploaded_at ? new Date(a.uploaded_at).getTime() : 0;
            const bTime = b?.uploaded_at ? new Date(b.uploaded_at).getTime() : 0;
            if (bTime !== aTime) return bTime - aTime;
            return Number(b?.id || 0) - Number(a?.id || 0);
        });

    const observationPhotos = item.attachments?.filter(a => a.type === "observation") || [];
    const actionPlanPhotos = sortAttachmentsNewestFirst(item.attachments?.filter(a => a.type === "action") || []);
    const compliancePhotos = sortAttachmentsNewestFirst(item.attachments?.filter(a => a.type === "compliance") || []);

    // Note: The attachment model does not contain attempt/round information. 
    // "Latest" is determined by the newest uploaded_at timestamp.
    const latestActionPlanPhoto = actionPlanPhotos[0] || null;
    const latestCompliancePhoto = compliancePhotos[0] || null;

    const attachmentHistory = sortAttachmentsNewestFirst(
        item.attachments?.filter(a => a.type === "action" || a.type === "compliance") || []
    );

    const attachmentTypeLabel = {
        action: "Action Plan Photo",
        compliance: "Compliance Photo",
    };

    const latestMakerResolveLog = sortLogsNewestFirst(
        item.action_logs?.filter(log => log.action === "resolved" && String(log.performed_by_role_name || "").trim().toLowerCase() === "maker") || []
    )[0] || null;
    const latestMakerRemark = latestMakerResolveLog?.remark?.trim() || "";

    const latestCheckerReviewLog = sortLogsNewestFirst(
        item.action_logs?.filter(log => log.action === "verified" && String(log.performed_by_role_name || "").trim().toLowerCase() === "checker") || []
    )[0] || null;
    const latestCheckerRemark = latestCheckerReviewLog?.remark?.trim() || "";

    const latestCheckerStatusChange = sortLogsNewestFirst(
        item.status_history?.filter(h => String(h.changed_by_role_name || "").trim().toLowerCase() === "checker" && h.from_status === "resolved" && (h.to_status === "closed" || h.to_status === "in_progress")) || []
    )[0] || null;
    const checkerReviewResult = latestCheckerStatusChange?.to_status === "closed" ? "Approved" : latestCheckerStatusChange?.to_status === "in_progress" ? "Rejected for Rework" : "";

    const handleAction = async (actionFn, payload = {}, successMessage) => {
        if (actionLoading) return;
        setActionLoading(true);
        try {
            await actionFn(item.id, {
                ...payload,
                performed_by_id: currentUser.user_id,
                performed_by_name: currentUser.username,
                performed_by_role_name: currentUser.role_name,
                remark: actionRemark
            });
            alert(successMessage);
            setActionRemark("");
            setActionTaken("");
            await reloadObservation();
        } catch (error) {
            console.error("Action failed", error);
            alert("Action failed: " + (error.response?.data?.error || error.message));
        } finally {
            setActionLoading(false);
        }
    };

    const removeSelectedFile = (type, index) => {
        if (type === "action") setActionPhotos(prev => prev.filter((_, i) => i !== index));
        if (type === "compliance") setCheckerCompliancePhotos(prev => prev.filter((_, i) => i !== index));
    };

    const uploadMakerPhotos = async () => {
        let allSuccess = true;
        for (let i = actionPhotos.length - 1; i >= 0; i--) {
            const file = actionPhotos[i];
            const formData = new FormData();
            formData.append("file", file);
            formData.append("type", "action");
            formData.append("uploaded_by_id", currentUser.user_id);
            formData.append("uploaded_by_name", currentUser.username);
            formData.append("uploaded_by_role_name", "maker");

            try {
                await uploadQCObservationAttachment(item.id, formData);
                setActionPhotos(prev => prev.filter((_, index) => index !== i));
            } catch (err) {
                console.error("Failed to upload action photo", err);
                allSuccess = false;
            }
        }
        return allSuccess;
    };

    const uploadCheckerCompliancePhotos = async () => {
        let allSuccess = true;
        for (let i = checkerCompliancePhotos.length - 1; i >= 0; i--) {
            const file = checkerCompliancePhotos[i];
            const formData = new FormData();
            formData.append("file", file);
            formData.append("type", "compliance");
            formData.append("uploaded_by_id", currentUser.user_id);
            formData.append("uploaded_by_name", currentUser.username);
            formData.append("uploaded_by_role_name", "checker");

            try {
                await uploadQCObservationAttachment(item.id, formData);
                setCheckerCompliancePhotos(prev => prev.filter((_, index) => index !== i));
            } catch (err) {
                console.error("Failed to upload compliance photo", err);
                allSuccess = false;
            }
        }
        return allSuccess;
    };

    const handleStartWork = () => handleAction(startQCObservationWork, {}, "Work started successfully.");

    const handleResolve = async () => {
        if (actionLoading) return;
        setActionLoading(true);
        const uploadsSuccessful = await uploadMakerPhotos();
        if (!uploadsSuccessful) {
            setActionLoading(false);
            alert("Some photos failed to upload. Please try again before resolving.");
            return;
        }

        try {
            await resolveQCObservation(item.id, {
                action_taken: actionTaken,
                performed_by_id: currentUser.user_id,
                performed_by_name: currentUser.username,
                performed_by_role_name: currentUser.role_name,
                remark: actionRemark
            });
            alert("Observation marked as resolved.");
            setActionRemark("");
            setActionTaken("");
            await reloadObservation();
        } catch (error) {
            console.error("Resolve failed", error);
            alert("Action failed: " + (error.response?.data?.error || error.message));
        } finally {
            setActionLoading(false);
        }
    };

    const str = (val) => String(val);

    const handleApprove = async () => {
        if (actionLoading || uploadingPhotos) return;

        if (item.status !== "resolved" || str(item.current_actor_id) !== str(currentUser.user_id)) {
            alert("State has changed. Please refresh.");
            return;
        }

        setActionLoading(true);
        setUploadingPhotos(true);

        try {
            const uploadsSuccessful = await uploadCheckerCompliancePhotos();
            if (!uploadsSuccessful) {
                setActionLoading(false);
                setUploadingPhotos(false);
                alert("Some compliance photos failed to upload. Please try again before approving.");
                return;
            }

            await reviewQCObservation(item.id, {
                result: "approved",
                performed_by_id: currentUser.user_id,
                performed_by_name: currentUser.username,
                performed_by_role_name: "checker",
                remark: actionRemark.trim(),
            });

            alert("Observation approved and closed successfully.");
            setActionRemark("");
            setCheckerCompliancePhotos([]);
            await reloadObservation();
        } catch (error) {
            console.error("Failed to approve observation", error.response?.data || error);
            alert(
                error.response?.data?.error ||
                error.response?.data?.details ||
                error.response?.data?.message ||
                error.message ||
                "Failed to approve observation"
            );
        } finally {
            setActionLoading(false);
            setUploadingPhotos(false);
        }
    };

    const handleReject = async () => {
        if (actionLoading || uploadingPhotos) return;

        if (!actionRemark.trim()) {
            alert("Checker remark is required when rejecting an observation.");
            return;
        }

        if (item.status !== "resolved" || str(item.current_actor_id) !== str(currentUser.user_id)) {
            alert("State has changed. Please refresh.");
            return;
        }

        setActionLoading(true);
        setUploadingPhotos(true);

        try {
            const uploadsSuccessful = await uploadCheckerCompliancePhotos();
            if (!uploadsSuccessful) {
                setActionLoading(false);
                setUploadingPhotos(false);
                alert("Some compliance photos failed to upload. Please try again before rejecting.");
                return;
            }

            await reviewQCObservation(item.id, {
                result: "rejected",
                performed_by_id: currentUser.user_id,
                performed_by_name: currentUser.username,
                performed_by_role_name: "checker",
                remark: actionRemark.trim(),
            });

            alert("Observation rejected and sent for rework.");
            setActionRemark("");
            setCheckerCompliancePhotos([]);
            await reloadObservation();
        } catch (error) {
            console.error("Failed to reject observation", error.response?.data || error);
            alert(
                error.response?.data?.error ||
                error.response?.data?.details ||
                error.response?.data?.message ||
                error.message ||
                "Failed to reject observation"
            );
        } finally {
            setActionLoading(false);
            setUploadingPhotos(false);
        }
    };

    return (
        <div className="bg-white rounded-xl border mb-6 overflow-hidden" style={{ borderColor: "#DEDAD1" }}>
            <div className="p-6 border-b flex justify-between items-center" style={{ borderColor: "#EFEDE7", backgroundColor: "#FBF8F3" }}>
                <h2 className="text-[18px] font-bold" style={{ color: "#1B2430" }}>
                    Item ID: {item.id} - {item.gap_type || "Item"}
                </h2>
                <StatusPill status={item.status} />
            </div>

            <div className="p-6">
                <Stepper currentStatus={item.status} />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
                    <div className="lg:col-span-2 space-y-6">
                        <div className="tag-corner bg-white border p-6" style={{ borderColor: "#DEDAD1" }}>
                            <h3 className="text-[15px] font-bold mb-4" style={{ color: "#1B2430" }}>Item Details</h3>

                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#8A8F79" }}>Gap Type</div>
                                    <div className="text-[14px]" style={{ color: "#3A4552" }}>{item.gap_type || "-"}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#8A8F79" }}>Repetition</div>
                                    <div className="text-[14px]" style={{ color: "#3A4552" }}>{item.repetition || "-"}</div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <div className="text-[12px] font-semibold uppercase mb-1" style={{ color: "#8A8F79" }}>Description</div>
                                    <div className="text-[14.5px] leading-relaxed" style={{ color: "#3A4552" }}>
                                        {item.description || "No description provided."}
                                    </div>
                                </div>

                                {item.resolution_advised && (
                                    <div>
                                        <div className="text-[12px] font-semibold uppercase mb-1" style={{ color: "#8A8F79" }}>Resolution Advised</div>
                                        <div className="text-[14.5px] leading-relaxed" style={{ color: "#3A4552" }}>
                                            {item.resolution_advised}
                                        </div>
                                    </div>
                                )}

                                {item.attachments?.filter(a => a.type === 'observation').length > 0 && (
                                    <div>
                                        <div className="text-[12px] font-semibold uppercase mb-2" style={{ color: "#8A8F79" }}>Observation Photos</div>
                                        <div className="flex gap-2 flex-wrap">
                                            {item.attachments.filter(a => a.type === 'observation').map(att => (
                                                <a key={att.id} href={resolveMediaUrl(att.file)} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded border overflow-hidden" style={{ borderColor: "#DEDAD1" }}>
                                                    <img src={resolveMediaUrl(att.file)} alt="Observation" className="w-full h-full object-cover" />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {latestActionPlanPhoto && (
                                    <div>
                                        <div className="text-[12px] font-semibold uppercase mb-2" style={{ color: "#8A8F79" }}>Latest Action Plan Photo</div>
                                        <a href={resolveMediaUrl(latestActionPlanPhoto.file)} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded border overflow-hidden" style={{ borderColor: "#DEDAD1" }}>
                                            <img src={resolveMediaUrl(latestActionPlanPhoto.file)} alt="Latest Action Plan" className="w-full h-full object-cover" />
                                        </a>
                                        <div className="text-[11px] mt-1" style={{ color: "#8A8F79" }}>
                                            Uploaded by {latestActionPlanPhoto.uploaded_by_name || "Maker"}
                                            {latestActionPlanPhoto.uploaded_at && (
                                                <>{" • "}{formatObservationDateTime(latestActionPlanPhoto.uploaded_at)}</>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {latestCompliancePhoto && (
                                    <div>
                                        <div className="text-[12px] font-semibold uppercase mb-2" style={{ color: "#8A8F79" }}>Latest Compliance Photo</div>
                                        <a href={resolveMediaUrl(latestCompliancePhoto.file)} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded border overflow-hidden" style={{ borderColor: "#DEDAD1" }}>
                                            <img src={resolveMediaUrl(latestCompliancePhoto.file)} alt="Latest Compliance" className="w-full h-full object-cover" />
                                        </a>
                                        <div className="text-[11px] mt-1" style={{ color: "#8A8F79" }}>
                                            Uploaded by {latestCompliancePhoto.uploaded_by_name || "Checker"}
                                            {latestCompliancePhoto.uploaded_at && (
                                                <>{" • "}{formatObservationDateTime(latestCompliancePhoto.uploaded_at)}</>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {latestMakerResolveLog && (
                            <div className="rounded-lg border p-4" style={{ borderColor: "#DEDAD1", background: "#FAF9F6" }}>
                                <div className="text-[12px] font-bold uppercase tracking-wide mb-3" style={{ color: "#8A8F79" }}>
                                    Maker Submission
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <div className="text-[11px] font-semibold uppercase" style={{ color: "#8A8F79" }}>Action Taken</div>
                                        <div className="text-[14px] mt-1" style={{ color: "#3A4552" }}>{item.action_taken || "-"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] font-semibold uppercase" style={{ color: "#8A8F79" }}>Maker Remark</div>
                                        <div className="text-[14px] mt-1 whitespace-pre-wrap" style={{ color: "#3A4552" }}>{latestMakerRemark || "-"}</div>
                                    </div>
                                    <div className="text-[11px]" style={{ color: "#8A8F79" }}>
                                        Submitted by {latestMakerResolveLog.performed_by_name || "Maker"}
                                        {latestMakerResolveLog.created_at && (
                                            <>{" • "}{formatObservationDateTime(latestMakerResolveLog.created_at)}</>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {latestCheckerReviewLog && (
                            <div className="rounded-lg border p-4" style={{ borderColor: "#DEDAD1", background: "#FAF9F6" }}>
                                <div className="text-[12px] font-bold uppercase tracking-wide mb-3" style={{ color: "#8A8F79" }}>
                                    Checker Review
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <div className="text-[11px] font-semibold uppercase" style={{ color: "#8A8F79" }}>Review Result</div>
                                        <div className="text-[14px] font-semibold mt-1" style={{ color: checkerReviewResult === "Approved" ? "#3F8358" : "#C1443A" }}>
                                            {checkerReviewResult || "-"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] font-semibold uppercase" style={{ color: "#8A8F79" }}>Checker Remark</div>
                                        <div className="text-[14px] mt-1 whitespace-pre-wrap" style={{ color: "#3A4552" }}>{latestCheckerRemark || "-"}</div>
                                    </div>
                                    <div className="text-[11px]" style={{ color: "#8A8F79" }}>
                                        Reviewed by {latestCheckerReviewLog.performed_by_name || "Checker"}
                                        {latestCheckerReviewLog.created_at && (
                                            <>{" • "}{formatObservationDateTime(latestCheckerReviewLog.created_at)}</>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {item.status !== "closed" && (
                            <div className="bg-white rounded-xl border p-6" style={{ borderColor: "#DEDAD1" }}>
                                <h3 className="text-[15px] font-bold mb-4 flex items-center gap-2" style={{ color: "#1B2430" }}>
                                    Take Action
                                </h3>

                                {hasActionControls ? (
                                    <div className="space-y-4">
                                        {((item.status === 'in_progress' && isClaimedMaker) || (item.status === 'resolved' && isCurrentChecker)) && (
                                            <div>
                                                <label className="block text-[13px] font-semibold mb-1.5" style={{ color: "#3A4552" }}>
                                                    {item.status === 'in_progress' ? 'Action Taken *' : 'Review Remarks'}
                                                </label>
                                                <textarea
                                                    value={item.status === 'in_progress' ? actionTaken : actionRemark}
                                                    onChange={e => item.status === 'in_progress' ? setActionTaken(e.target.value) : setActionRemark(e.target.value)}
                                                    rows="3"
                                                    className="w-full rounded-lg border px-3 py-2 text-[14px] resize-none"
                                                    style={{ borderColor: "#DEDAD1" }}
                                                    placeholder={item.status === 'in_progress' ? "Describe the fix..." : "Add any review remarks..."}
                                                />
                                                {item.status === 'in_progress' && isMaker && (
                                                    <div className="mt-4 space-y-4">
                                                        <div>
                                                            <label className="block text-[13px] font-semibold mb-2" style={{ color: "#3A4552" }}>
                                                                Action Plan Photos
                                                            </label>
                                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                                <label
                                                                    htmlFor="action-photo-gallery"
                                                                    className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700 transition-colors hover:bg-orange-100"
                                                                >
                                                                    <UploadCloud className="h-4 w-4" />
                                                                    Select from Gallery
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setCameraTarget("action")}
                                                                    className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-orange-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-orange-700"
                                                                >
                                                                    <Camera className="h-4 w-4" />
                                                                    Take Photo
                                                                </button>
                                                            </div>
                                                            <input
                                                                id="action-photo-gallery"
                                                                type="file"
                                                                multiple
                                                                accept="image/*"
                                                                onChange={(e) => {
                                                                    if (e.target.files?.length) {
                                                                        addActionPhotos(e.target.files);
                                                                    }
                                                                    e.target.value = "";
                                                                }}
                                                                className="hidden"
                                                            />
                                                            {actionPhotos.length > 0 && (
                                                                <div className="flex gap-2 mt-2 flex-wrap">
                                                                    {actionPhotos.map((f, i) => (
                                                                        <SelectedImagePreview
                                                                            key={i}
                                                                            file={f}
                                                                            alt="Preview"
                                                                            onRemove={() => removeSelectedFile('action', i)}
                                                                            onAnnotate={() => openActionPhotoAnnotation(f, i)}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {item.status === 'resolved' && isChecker && (
                                                    <div className="mt-4 space-y-4">
                                                        <div>
                                                            <label className="block text-[13px] font-semibold mb-2" style={{ color: "#3A4552" }}>
                                                                Compliance Photos (Optional)
                                                            </label>
                                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                                <label
                                                                    htmlFor="compliance-photo-gallery"
                                                                    className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700 transition-colors hover:bg-orange-100"
                                                                >
                                                                    <UploadCloud className="h-4 w-4" />
                                                                    Select from Gallery
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setCameraTarget("compliance")}
                                                                    className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-orange-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-orange-700"
                                                                >
                                                                    <Camera className="h-4 w-4" />
                                                                    Take Photo
                                                                </button>
                                                            </div>
                                                            <input
                                                                id="compliance-photo-gallery"
                                                                type="file"
                                                                multiple
                                                                accept="image/*"
                                                                onChange={(e) => {
                                                                    if (e.target.files?.length) {
                                                                        addCompliancePhotos(e.target.files);
                                                                    }
                                                                    e.target.value = "";
                                                                }}
                                                                className="hidden"
                                                            />
                                                            {checkerCompliancePhotos.length > 0 && (
                                                                <div className="flex gap-2 mt-2 flex-wrap">
                                                                    {checkerCompliancePhotos.map((f, i) => (
                                                                        <SelectedImagePreview
                                                                            key={i}
                                                                            file={f}
                                                                            alt="Preview"
                                                                            onRemove={() => removeSelectedFile('compliance', i)}
                                                                            onAnnotate={() => openCompliancePhotoAnnotation(f, i)}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {item.status === 'pending' && (
                                            <textarea
                                                value={actionRemark}
                                                onChange={e => setActionRemark(e.target.value)}
                                                rows="2"
                                                className="w-full rounded-lg border px-3 py-2 text-[14px] resize-none mb-2"
                                                style={{ borderColor: "#DEDAD1" }}
                                                placeholder="Optional remarks before starting..."
                                            />
                                        )}

                                        <div className="flex gap-3 pt-2">
                                            {(item.status === 'pending' && isActiveAssignedMaker) && (
                                                <button onClick={handleStartWork} disabled={actionLoading} className="px-5 py-2.5 rounded-lg font-semibold text-[14px] text-white transition-opacity hover:opacity-90 flex-1" style={{ background: "#E07A1F" }}>
                                                    Start Work
                                                </button>
                                            )}

                                            {(item.status === 'in_progress' && isClaimedMaker) && (
                                                <button onClick={handleResolve} disabled={actionLoading || !actionTaken.trim()} className="px-5 py-2.5 rounded-lg font-semibold text-[14px] text-white transition-opacity hover:opacity-90 flex-1" style={{ background: "#3F8358" }}>
                                                    Resolve Observation
                                                </button>
                                            )}

                                            {(item.status === 'resolved' && isCurrentChecker) && (
                                                <>
                                                    <button onClick={handleApprove} disabled={actionLoading || uploadingPhotos} className="px-5 py-2.5 rounded-lg font-semibold text-[14px] text-white transition-opacity hover:opacity-90 flex-1" style={{ background: "#3F8358" }}>
                                                        {uploadingPhotos ? "Uploading Photos..." : actionLoading ? "Approving..." : "Approve & Close"}
                                                    </button>
                                                    <button onClick={handleReject} disabled={actionLoading || uploadingPhotos} className="px-5 py-2.5 rounded-lg font-semibold text-[14px] text-white transition-opacity hover:opacity-90 flex-1" style={{ background: "#C1443A" }}>
                                                        {uploadingPhotos ? "Uploading Photos..." : actionLoading ? "Rejecting..." : "Reject (Rework)"}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-[14px] font-medium p-4 rounded-lg bg-[#F6F5F1] text-center" style={{ color: "#6B7480" }}>
                                        {isMaker && item.status === 'in_progress' && !isClaimedMaker ? (
                                            <>This item is being handled by <strong>{item.current_actor_name || item.current_actor_role_name || "another Maker"}</strong>.</>
                                        ) : (
                                            <>Waiting for <strong>{item.current_actor_name || item.current_actor_role_name || "Assignee"}</strong> to take action.</>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="space-y-6">
                        <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#DEDAD1" }}>
                            <h3 className="text-[13px] font-bold uppercase tracking-wide mb-4" style={{ color: "#8A8F79" }}>Assignees</h3>
                            <div className="space-y-3">
                                {item.assignments?.length > 0 ? item.assignments.map(ass => (
                                    <div key={ass.id || ass.user_id} className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-[#EFEDE7] flex items-center justify-center text-[#3A4552]">
                                            <UserCircle size={18} />
                                        </div>
                                        <div>
                                            <div className="text-[14px] font-semibold" style={{ color: "#1B2430" }}>
                                                {ass.user_name}
                                                {ass.is_active && !ass.is_skipped && item.status === 'pending' && <span className="ml-2 text-[10px] bg-[#DCEBE1] text-[#3F8358] px-1.5 py-0.5 rounded font-bold uppercase">Available</span>}
                                                {ass.is_active && !ass.is_skipped && item.status !== 'pending' && String(item.current_actor_id) === String(ass.user_id) && <span className="ml-2 text-[10px] bg-[#DCEBE1] text-[#3F8358] px-1.5 py-0.5 rounded font-bold uppercase">Active</span>}
                                                {ass.is_skipped && <span className="ml-2 text-[10px] bg-[#E7E5E0] text-[#6B7280] px-1.5 py-0.5 rounded font-bold uppercase">Skipped</span>}
                                            </div>
                                            <div className="text-[12px] capitalize" style={{ color: "#6B7480" }}>{ass.role_name}</div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="text-[13px] italic text-[#9A968C]">No assignees yet.</div>
                                )}
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#DEDAD1" }}>
                            <h3 className="text-[13px] font-bold uppercase tracking-wide mb-4" style={{ color: "#8A8F79" }}>Activity Log</h3>
                            <div className="space-y-5">
                                {item.action_logs?.length > 0 ? item.action_logs.map(log => (
                                    <div key={log.id} className="flex gap-3 relative">
                                        <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10" style={{ background: "#F6E6CE", color: "#B4761F" }}>
                                            <MessageSquare size={12} />
                                        </div>
                                        <div className="absolute top-6 bottom-[-20px] left-3 w-[2px] bg-[#EFEDE7] z-0 last:hidden"></div>

                                        <div className="pb-1">
                                            <div className="text-[13.5px] font-semibold" style={{ color: "#1B2430" }}>
                                                {log.performed_by_name} <span className="font-normal text-[#6B7480]">({ACTION_LABELS[log.action] || log.action})</span>
                                            </div>
                                            <div className="text-[12.5px] mt-1" style={{ color: "#3A4552" }}>
                                                {log.remark || "No remarks."}
                                            </div>
                                            <div className="text-[11px] font-medium mt-1.5 uppercase tracking-wide" style={{ color: "#8A8F79" }}>
                                                {formatObservationDateTime(log.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="text-[13px] italic text-[#9A968C]">No activity recorded yet.</div>
                                )}
                            </div>
                        </div>
                        {attachmentHistory.length > 0 && (
                            <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#DEDAD1" }}>
                                <h3 className="text-[13px] font-bold uppercase tracking-wide mb-4" style={{ color: "#8A8F79" }}>Attachment History</h3>
                                <div className="space-y-4">
                                    {attachmentHistory.map((att, idx) => (
                                        <div key={idx} className="flex gap-3 items-center">
                                            <a href={resolveMediaUrl(att.file)} target="_blank" rel="noreferrer" className="block w-12 h-12 shrink-0 rounded border overflow-hidden" style={{ borderColor: "#DEDAD1" }}>
                                                <img src={resolveMediaUrl(att.file)} alt={attachmentTypeLabel[att.type] || "Photo"} className="w-full h-full object-cover" />
                                            </a>
                                            <div>
                                                <div className="text-[13px] font-semibold" style={{ color: "#1B2430" }}>{attachmentTypeLabel[att.type] || "Photo"}</div>
                                                <div className="text-[12px]" style={{ color: "#3A4552" }}>
                                                    {att.uploaded_by_name || "User"} <span className="text-[#8A8F79] capitalize">({att.uploaded_by_role_name || "Role"})</span>
                                                </div>
                                                <div className="text-[11px] font-medium mt-0.5 uppercase tracking-wide" style={{ color: "#8A8F79" }}>
                                                    {formatObservationDateTime(att.uploaded_at)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <SafetyImageAnnotationModal
                open={annotationQueue.length > 0}
                file={annotationQueue[annotationQueueIndex]?.file || null}
                onClose={closeAnnotationQueue}
                onSave={handleAnnotationSave}
                currentIndex={annotationQueueIndex}
                totalCount={annotationQueue.length}
                onPrevious={annotationQueueIndex > 0 ? handleAnnotationPrevious : undefined}
                onNext={annotationQueueIndex < annotationQueue.length - 1 ? handleAnnotationNext : undefined}
                onSkip={handleAnnotationSkip}
            />

            <SafetyCameraCaptureModal
                open={Boolean(cameraTarget)}
                onClose={() => setCameraTarget(null)}
                onCapture={handleCameraCapture}
                title={cameraTarget === "action" ? "Take Action Plan Photo" : "Take Compliance Photo"}
            />
        </div>
    );
}

export default function ReviewObservation({ currentUser: propUser }) {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const targetItemId = searchParams.get("item");
    const navigate = useNavigate();

    const currentUser = useMemo(() => {
        if (propUser) return propUser;
        try {
            const data = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
            const roles = (data.roles || []).map(r => String(r).toUpperCase());
            const role = roles.includes("CHECKER") ? "checker" : (roles.includes("MAKER") ? "maker" : "");
            return {
                user_id: data.user_id,
                username: data.username,
                role_name: role || (localStorage.getItem("ROLE") || "").toLowerCase(),
            };
        } catch (e) {
            return { role_name: (localStorage.getItem("ROLE") || "").toLowerCase() };
        }
    }, [propUser]);

    const [observation, setObservation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reportDownloading, setReportDownloading] = useState(false);
    const [reportViewing, setReportViewing] = useState(false);
    const [enrichedProjectName, setEnrichedProjectName] = useState("");

    const resolvedProjectName = useMemo(() => {
        if (enrichedProjectName) return enrichedProjectName;
        const rawName = observation?.project_name;
        if (rawName && typeof rawName === "string" && !/^project\s*#?\s*\d+$/i.test(rawName.trim())) {
            return rawName.trim();
        }
        const pid = observation?.project;
        if (pid) {
            const activePid = localStorage.getItem("ACTIVE_PROJECT_ID") || localStorage.getItem("PROJECT_ID");
            const activePname = localStorage.getItem("ACTIVE_PROJECT_NAME") || localStorage.getItem("PROJECT_NAME");
            if (activePid && String(activePid) === String(pid) && activePname && !/^project\s*#?\s*\d+$/i.test(activePname.trim())) {
                return activePname.trim();
            }
            try {
                const cached = JSON.parse(
                    localStorage.getItem("projects") ||
                    localStorage.getItem("PROJECTS") ||
                    localStorage.getItem("PROJECT_LIST") ||
                    "[]"
                );
                if (Array.isArray(cached)) {
                    const match = cached.find((cp) => String(cp?.id || cp?.project_id) === String(pid));
                    const matchName = match?.name || match?.project_name || match?.title;
                    if (matchName && !/^project\s*#?\s*\d+$/i.test(String(matchName).trim())) {
                        return String(matchName).trim();
                    }
                }
            } catch {}
        }
        return rawName || (pid ? `Project ${pid}` : "-");
    }, [observation, enrichedProjectName]);

    useEffect(() => {
        const rawName = observation?.project_name;
        const pid = observation?.project;
        if (pid && (!rawName || /^project\s*#?\s*\d+$/i.test(String(rawName).trim()))) {
            getProjectsForCurrentUser()
                .then((res) => {
                    const list = res?.data || [];
                    if (Array.isArray(list)) {
                        const match = list.find((p) => String(p?.id || p?.project_id) === String(pid));
                        const realName = match?.name || match?.project_name || match?.title;
                        if (realName && !/^project\s*#?\s*\d+$/i.test(String(realName).trim())) {
                            setEnrichedProjectName(String(realName).trim());
                        }
                    }
                })
                .catch(() => {});
        }
    }, [observation?.project, observation?.project_name]);

    const allItems = Array.isArray(observation?.items) ? observation.items : [];
    const observationItems = useMemo(() => {
        if (!targetItemId) return allItems;
        const matched = allItems.filter(
            (item) => String(item.id) === String(targetItemId)
        );
        return matched.length > 0 ? matched : allItems;
    }, [allItems, targetItemId]);

    const hasInProgressItem = observationItems.some(
        (item) => normalizeStatusKey(item.status) === "in_progress"
    );
    const hasResolvedItem = observationItems.some(
        (item) => normalizeStatusKey(item.status) === "resolved"
    );
    const hasPendingItem = observationItems.some(
        (item) => normalizeStatusKey(item.status) === "pending"
    );
    const areAllItemsClosed =
        observationItems.length > 0 &&
        observationItems.every((item) => normalizeStatusKey(item.status) === "closed");

    // Robust effective overall status resolution matching list API & item statuses
    const effectiveOverallStatus = useMemo(() => {
        const raw = normalizeStatusKey(observation?.overall_status || observation?.status);
        if (raw) return raw;
        if (areAllItemsClosed) return "closed";
        if (hasInProgressItem) return "in_progress";
        if (hasResolvedItem) return "resolved";
        if (hasPendingItem) return "pending";
        return "pending";
    }, [observation, areAllItemsClosed, hasInProgressItem, hasResolvedItem, hasPendingItem]);

    const isObservationClosed =
        effectiveOverallStatus === "closed" || areAllItemsClosed;

    const isObservationInProgress =
        effectiveOverallStatus === "in_progress" || hasInProgressItem;

    // Report is a snapshot of the observation at any status
    const canDownloadReport = Boolean(observation?.id);

    const sanitizeFilename = (value) =>
        String(value || "observation-report")
            .trim()
            .replace(/[^a-zA-Z0-9_-]+/g, "-")
            .replace(/^-+|-+$/g, "");

    const extractErrorMessage = async (error, fallback = "Failed to process observation report") => {
        const errorData = error?.response?.data;
        if (errorData instanceof Blob) {
            try {
                const errorText = await errorData.text();
                const parsed = JSON.parse(errorText);
                return parsed.error || parsed.details || parsed.message || fallback;
            } catch {
                try {
                    const rawText = await errorData.text();
                    if (rawText && rawText.length < 300 && !rawText.trim().startsWith("<")) {
                        return rawText;
                    }
                } catch {
                    // ignore
                }
            }
        } else if (errorData) {
            return (
                errorData.error ||
                errorData.details ||
                errorData.message ||
                (typeof errorData === "string" ? errorData : fallback)
            );
        }
        return error?.message || fallback;
    };

    const parsePdfResponseBlob = async (response) => {
        const contentType = (response?.headers?.["content-type"] || "").toLowerCase();

        if (contentType.includes("json")) {
            let errorMsg = "Failed to generate report";
            if (response.data instanceof Blob) {
                try {
                    const text = await response.data.text();
                    const parsed = JSON.parse(text);
                    errorMsg = parsed.error || parsed.details || parsed.message || errorMsg;
                } catch {
                    // ignore
                }
            }
            throw new Error(errorMsg);
        }

        const rawBlob = response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: "application/pdf" });

        // Validate %PDF- signature
        const headerBuffer = await rawBlob.slice(0, 5).arrayBuffer();
        const headerText = new TextDecoder().decode(headerBuffer);
        if (!headerText.startsWith("%PDF-")) {
            const text = await rawBlob.text();
            try {
                const parsed = JSON.parse(text);
                throw new Error(
                    parsed.error ||
                    parsed.details ||
                    parsed.message ||
                    "The server did not return a valid PDF report."
                );
            } catch (parseErr) {
                if (
                    parseErr.message &&
                    parseErr.message !== "Unexpected end of JSON input" &&
                    !parseErr.message.includes("JSON")
                ) {
                    throw parseErr;
                }
                throw new Error("The server did not return a valid PDF report.");
            }
        }

        return new Blob([rawBlob], { type: "application/pdf" });
    };

    const handleDownloadReport = async () => {
        if (reportDownloading || !observation?.id || !canDownloadReport) return;

        setReportDownloading(true);

        try {
            const response = await downloadQCObservationReport(observation.id);
            const blob = await parsePdfResponseBlob(response);

            let filename = "";
            const contentDisposition = response.headers?.["content-disposition"];
            if (contentDisposition) {
                const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
                const normalMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
                filename = utf8Match?.[1] ? decodeURIComponent(utf8Match[1]) : normalMatch?.[1] || "";
            }

            if (!filename) {
                const dateStr = observation.created_at
                    ? new Date(observation.created_at).toISOString().split("T")[0]
                    : "report";
                filename = `QC_Observation_${observation.id}_${dateStr}.pdf`;
            }

            const objectUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = objectUrl;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.setTimeout(() => { window.URL.revokeObjectURL(objectUrl); }, 1000);
        } catch (error) {
            console.error("Failed to download observation report", error);
            const message = await extractErrorMessage(error, "Failed to download QC Observation report.");
            alert(message);
        } finally {
            setReportDownloading(false);
        }
    };

    const handleViewReport = async () => {
        if (reportViewing || reportDownloading || !observation?.id || !canDownloadReport) return;

        // Open a new tab immediately on user click to prevent browser popup blockers
        let newTab = null;
        try {
            newTab = window.open("about:blank", "_blank");
        } catch {
            // Popup blocked or not supported
        }

        setReportViewing(true);

        try {
            const response = await downloadQCObservationReport(observation.id, { inline: 1 });
            const blob = await parsePdfResponseBlob(response);

            const objectUrl = window.URL.createObjectURL(blob);
            if (newTab && !newTab.closed) {
                newTab.location.href = objectUrl;
            } else {
                window.open(objectUrl, "_blank");
            }

            window.setTimeout(() => {
                window.URL.revokeObjectURL(objectUrl);
            }, 120000);
        } catch (error) {
            if (newTab && !newTab.closed) {
                newTab.close();
            }
            console.error("Failed to view observation report", error);
            const message = await extractErrorMessage(error, "Failed to view QC Observation report.");
            alert(message);
        } finally {
            setReportViewing(false);
        }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await getQCObservationDetails(id);
            setObservation(response.data);
        } catch (error) {
            console.error("Failed to fetch observation details", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen p-8 bg-[#F6F5F1] flex items-center justify-center font-sans text-[#9A968C]">
                Loading observation details...
            </div>
        );
    }

    if (!observation) {
        return (
            <div className="min-h-screen p-8 bg-[#F6F5F1] flex items-center justify-center font-sans text-[#C1443A] font-bold">
                Observation not found.
            </div>
        );
    }

    const displayId = `OBS-${String(observation.id).padStart(4, "0")}`;

    return (
        <div className="min-h-screen p-8 font-sans" style={{ background: "#F6F5F1" }}>
            <style>{`
                .tag-corner {
                    clip-path: polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 0 100%);
                    border-radius: 4px;
                }
                textarea:focus {
                    box-shadow: 0 0 0 3px rgba(224, 122, 31, 0.18);
                    border-color: #E07A1F !important;
                    outline: none;
                }
            `}</style>

            <div className="max-w-[1000px] mx-auto">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1.5 text-[13.5px] font-semibold mb-6"
                    style={{ color: "#6B7480" }}
                >
                    <ArrowLeft size={16} />
                    Back
                </button>

                {/* Parent Header Card */}
                <div className="bg-white rounded-xl border p-6 mb-8" style={{ borderColor: "#DEDAD1" }}>
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-[12px] font-semibold tracking-widest uppercase mb-1" style={{ color: "#E07A1F" }}>
                                {targetItemId ? `${displayId} · Item #${targetItemId}` : displayId}
                            </div>
                            <h1 className="text-[28px] font-bold leading-tight" style={{ color: "#1B2430" }}>
                                {observation.observation_name || "Observation"}
                            </h1>
                            <div className="text-[14px] font-medium mt-2" style={{ color: "#6B7480" }}>
                                {resolvedProjectName} • {observation.tower_name} • {observation.floor_name || observation.custom_location}
                            </div>
                            <div className="flex gap-8 mt-4">
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#8A8F79" }}>Category</div>
                                    <div className="text-[13px] font-semibold" style={{ color: "#3A4552" }}>{observation.category_name || "-"}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#8A8F79" }}>Total Items</div>
                                    <div className="text-[13px] font-semibold" style={{ color: "#3A4552" }}>{observationItems.length}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#8A8F79" }}>Created At</div>
                                    <div className="text-[13px] font-semibold" style={{ color: "#3A4552" }}>{formatObservationDateTime(observation.created_at)}</div>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {canDownloadReport && (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleViewReport}
                                        disabled={reportViewing || reportDownloading}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-[14px] border transition-all shadow-sm"
                                        style={{
                                            borderColor: "#E07A1F",
                                            color: "#E07A1F",
                                            backgroundColor: reportViewing ? "#FDF6ED" : "#FFFFFF",
                                            cursor: reportViewing || reportDownloading ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        <Eye size={16} />
                                        {reportViewing ? "Opening Report..." : "View Report"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDownloadReport}
                                        disabled={reportDownloading || reportViewing}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-[14px] text-white transition-opacity shadow-sm"
                                        style={{
                                            background: reportDownloading ? "#B7BEC9" : "#E07A1F",
                                            cursor: reportDownloading || reportViewing ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        <Download size={16} />
                                        {reportDownloading ? "Generating Report..." : "Download Report"}
                                    </button>
                                </>
                            )}
                            <StatusPill status={effectiveOverallStatus} />
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    {observationItems.map((item) => (
                        <ObservationItemCard
                            key={item.id}
                            item={item}
                            currentUser={currentUser}
                            reloadObservation={loadData}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}